/*
 * detectCountryApi
 * ----------------
 * Best-effort detection of the user's ISO 3166-1 alpha-2 country code so
 * `PhoneInput` can pre-select the appropriate dial-code prefix
 * (e.g. `+1` for a visitor loading the site from Canada, `+44` from the UK,
 * `+91` from India). Two-step waterfall:
 *
 *   1. **IP-based lookup** via https://api.country.is/ — free, no key
 *      required, tiny (~30-byte) response, CORS-enabled. This is the
 *      most accurate signal for physical location.
 *   2. **Locale-based guess** using `navigator.language(s)` — imperfect
 *      (a user with system locale `en-US` living in Toronto will
 *      resolve to US) but a decent fallback when the IP call fails
 *      (blocked network, offline, corporate proxy, ad-blocker rule).
 *
 * Result is cached at module scope: subsequent PhoneInput mounts reuse
 * the resolved ISO without triggering a second network call. The
 * cached value can be `null` when detection legitimately fails — we
 * store the "we already tried" state as a distinct value from
 * "not yet queried" via `undefined`.
 */

// undefined = not yet queried; null = queried but couldn't resolve.
let cachedIso: string | null | undefined = undefined;
let inflight: Promise<string | null> | null = null;

const IP_LOOKUP_URL = 'https://api.country.is/';
// Kept short so a stalled request doesn't hold up the phone-input
// initial render — if the API is slow we fall back to the locale
// guess and move on.
const REQUEST_TIMEOUT_MS = 2500;

/**
 * Resolve the user's country ISO code. Cached across the app; call it
 * from as many places as you like. Never throws — always resolves to a
 * 2-letter uppercase ISO code or `null`.
 */
export async function detectUserCountry(): Promise<string | null> {
  if (cachedIso !== undefined) return cachedIso;
  if (inflight) return inflight;

  inflight = performDetection()
    .then((iso) => {
      cachedIso = iso;
      return iso;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

async function performDetection(): Promise<string | null> {
  const ipCountry = await ipLookup().catch(() => null);
  if (ipCountry) return ipCountry;
  return localeGuess();
}

async function ipLookup(): Promise<string | null> {
  if (typeof fetch === 'undefined') return null;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(IP_LOOKUP_URL, { signal: controller.signal });
    if (!res.ok) return null;
    const data = (await res.json()) as { country?: unknown };
    const iso =
      typeof data.country === 'string' ? data.country.trim().toUpperCase() : null;
    return iso && /^[A-Z]{2}$/.test(iso) ? iso : null;
  } catch {
    // Network error, abort, blocked by ad-blocker — treat as
    // "unknown" and fall through to the locale guess.
    return null;
  } finally {
    clearTimeout(timer);
  }
}

function localeGuess(): string | null {
  if (typeof navigator === 'undefined') return null;
  const candidates: string[] = [];
  if (Array.isArray(navigator.languages)) {
    for (const l of navigator.languages) {
      if (typeof l === 'string') candidates.push(l);
    }
  }
  if (typeof navigator.language === 'string') candidates.push(navigator.language);
  for (const lang of candidates) {
    // Locale format: `en-US`, `en_US`, `fr-CA`, `en-GB`, etc.
    const parts = lang.split(/[-_]/);
    if (parts.length >= 2) {
      const region = parts[1].toUpperCase();
      if (/^[A-Z]{2}$/.test(region)) return region;
    }
  }
  return null;
}

/**
 * Escape hatch for tests. Clears the cached ISO so the next call
 * triggers a fresh detection. Not exported from any barrel — reach
 * for it from a test file.
 */
export function __resetCountryDetectionCache(): void {
  cachedIso = undefined;
  inflight = null;
}
